/**
 * What: Runs connection actions for the captured Shopify shop and activation.
 * Transport / data-flow: App Bridge bootstrap -> merchant adapter; shared readiness is refetched after every unlink attempt.
 * Invariants: Unknown acknowledgements never restore cached store data; obsolete callbacks cannot target a replacement store.
 * Settings owns action feedback through its page lifecycle; the shell retains its existing connect mutation wrapper.
 */
import { requestShopifyNavigation } from '../shopifyNavigation'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import type { useAppBridge } from '@shopify/app-bridge-react'
import {
  captureAuthenticatedShopBootstrap,
  getAppBridgeShop,
  ShopifyBootstrapScopeError,
} from '../authenticatedShopBootstrap'
import { reportClientDiagnostic } from '../clientDiagnostics'
import { shopifyApiFetch } from '../shopifyApiFetch'
import { describeErrorForDiagnostics, isScopeGuardRejection, parseContractPayload } from '../merchantErrors'
import { throwMerchantFacingApiError } from '../shopifyApiError'
import type { VerifiedTestFeedConnectionScope } from '../ShopifyAppContext'
import {
  evictShopifyConnectionData,
  shopifyConnectionQueryKey,
  type ShopifyConnectionSnapshot,
  type ShopifyDisconnectTarget,
} from './useShopifyConnection'
import { buildStudyBuilderInitialStateQueryKey } from './study-builder/studyBuilderStatus'
import { readTestFeedConnectionStatus } from './shopifyConnectionStatus'
import * as z from 'zod'
import { getShopifyJourney } from '../shopifyJourney'
import { trackShopifyAction } from '../shopifyUiTracking'

const connectionIntentSchema = z.object({ connectUrl: z.string().url() })

export function useShopifyConnectionMutations({
  shopify,
  verifiedShop,
  connectionScope,
  activeShopScopeKey,
  isActiveShopScope,
}: {
  shopify: ReturnType<typeof useAppBridge>
  verifiedShop: string | null
  connectionScope: VerifiedTestFeedConnectionScope | null
  activeShopScopeKey: string
  isActiveShopScope: (scopeKey: string) => boolean
}) {
  const queryClient = useQueryClient()
  const assertCurrent = (signal?: AbortSignal) => {
    signal?.throwIfAborted()
    if (!verifiedShop || getAppBridgeShop(shopify) !== verifiedShop || !isActiveShopScope(activeShopScopeKey))
      throw new ShopifyBootstrapScopeError('Verified Shopify shop scope changed.')
  }

  const connectTestFeed = async (signal?: AbortSignal) => {
    assertCurrent(signal)
    const bootstrap = await captureAuthenticatedShopBootstrap(shopify, { expectedShop: verifiedShop, signal })
    assertCurrent(signal)
    const journeyId = getShopifyJourney(bootstrap.shopDomain)
    const response = await shopifyApiFetch(
      bootstrap.authenticatedShopify,
      `/api/testfeed-connection-intent?journeyId=${journeyId}`,
      {
        init: { signal },
      },
    )
    if (!response.ok) return throwMerchantFacingApiError(response, 'connect-intent')
    const intent = parseContractPayload('shopify-connection-intent', connectionIntentSchema, await response.json())
    assertCurrent(signal)
    const left = await requestShopifyNavigation(shopify, () => {
      assertCurrent(signal)
      globalThis.window?.open(intent.connectUrl, '_top', 'noopener')
    })
    if (!left) throw new DOMException('Navigation cancelled', 'AbortError')
  }

  const disconnectTestFeed = async (target: ShopifyDisconnectTarget, signal: AbortSignal) => {
    assertCurrent(signal)
    const bootstrap = await captureAuthenticatedShopBootstrap(shopify, { expectedShop: verifiedShop, signal })
    const key = shopifyConnectionQueryKey(bootstrap.shopDomain)
    await queryClient.cancelQueries({ queryKey: key })
    if (connectionScope) await evictShopifyConnectionData(queryClient, connectionScope)
    assertCurrent(signal)
    const current = queryClient.getQueryData<ShopifyConnectionSnapshot>(key)
    if (
      current?.readiness.connectionId !== target.connectionId ||
      current.readiness.connectionActivatedAt !== target.expectedActivatedAt
    )
      throw new Error('SHOPIFY_CONNECTION_DISCONNECT_CONFLICT: Connection changed before dispatch')
    // Preserve only a recovery selector, not a credential or completion claim. If the POST's
    // response is lost, navigation/reload must retain the uncertainty for this exact activation.
    const browserWindow = globalThis.window
    const hadUnconfirmedDisconnect = Boolean(
      current.disconnectTarget || new URLSearchParams(browserWindow?.location.search).get('disconnectActivatedAt'),
    )
    if (browserWindow) {
      const url = new URL(browserWindow.location.href)
      url.searchParams.set('cleanupConnectionId', target.connectionId)
      url.searchParams.set('disconnectActivatedAt', target.expectedActivatedAt)
      browserWindow.history.replaceState(null, '', url)
    }
    queryClient.setQueryData<ShopifyConnectionSnapshot>(key, { ...current, scope: null, disconnectTarget: target })
    let dispatched = false
    let refused = false
    try {
      const response = await shopifyApiFetch(bootstrap.authenticatedShopify, '/api/testfeed-connection-disconnect', {
        onPhase: (phase) => {
          if (phase === 'request') dispatched = true
        },
        init: {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(target),
          signal,
        },
      })
      // A completed first-attempt client refusal has no server work left to overtake the read.
      // A refused retry cannot resolve an earlier lost POST; server failures stay unconfirmed too.
      refused = !response.ok && response.status < 500
      if (!response.ok) return await throwMerchantFacingApiError(response, 'disconnect')
      const result = await readTestFeedConnectionStatus(response)
      if (
        result.ready ||
        result.connectionId !== target.connectionId ||
        result.connectionActivatedAt !== target.expectedActivatedAt ||
        result.externalAccountId !== bootstrap.shopDomain ||
        result.connectionStatus !== 'disabled'
      )
        throw new Error('SHOPIFY_CONNECTION_DISCONNECT_CONFLICT: Invalid unlink acknowledgement')
      return result
    } finally {
      await queryClient.cancelQueries({ queryKey: key })
      if ((!dispatched || refused) && !hadUnconfirmedDisconnect) {
        queryClient.setQueryData<ShopifyConnectionSnapshot>(key, (snapshot) => {
          if (
            snapshot?.disconnectTarget?.connectionId !== target.connectionId ||
            snapshot.disconnectTarget.expectedActivatedAt !== target.expectedActivatedAt
          )
            return snapshot
          return { ...snapshot, disconnectTarget: undefined }
        })
        if (browserWindow && getAppBridgeShop(shopify) === bootstrap.shopDomain) {
          const url = new URL(browserWindow.location.href)
          if (
            url.searchParams.get('cleanupConnectionId') === target.connectionId &&
            url.searchParams.get('disconnectActivatedAt') === target.expectedActivatedAt
          ) {
            url.searchParams.delete('disconnectActivatedAt')
            browserWindow.history.replaceState(null, '', url)
          }
        }
      }
      // Navigation cancels the page, not a dispatched server write. Reconcile in the shared query;
      // an unchanged active read keeps polling and never releases the quarantined product scope.
      if (connectionScope) await evictShopifyConnectionData(queryClient, connectionScope)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: key }),
        queryClient.invalidateQueries({ queryKey: buildStudyBuilderInitialStateQueryKey(bootstrap.shopDomain) }),
      ])
    }
  }

  // Only the pre-account shell uses this wrapper; Settings runs the same operation through lifecycle.run.
  const connect = useMutation({
    mutationFn: () => trackShopifyAction(shopify, 'connect', () => connectTestFeed()),
    onError: (error) => {
      console.error(describeErrorForDiagnostics(error))
      if (!isScopeGuardRejection(error) && !(error instanceof ShopifyBootstrapScopeError))
        reportClientDiagnostic(shopify, { schema: 'shopify-connection-connect', paths: ['connect_failed'] })
    },
  })
  return {
    connectTestFeed,
    disconnectTestFeed,
    handleConnectTestFeed: useCallback(() => connect.mutate(), [connect.mutate]),
    isConnectingTestFeed: connect.isPending,
  }
}
